import {
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@mui/material'
import { useAppSelector } from '@/hooks'
import { stateDeviceSelector } from '@/modules/device-module/store/stateSlice'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import SimpleLineChart, { type LineSeries } from './SimpleLineChart'
import { toChartPoints } from './mockData'
import {
  type TrafficDetectorsChartsData,
  usePostTrafficDetectorsChartsMutation,
} from './trafficDetectorsChartsService'

const CHART_OPTIONS = [
  { label: 'Транспорт', value: '1', color: '#0f8fff' },
  { label: 'Обнаружений', value: '2', color: '#e450ed' },
  { label: 'Время занятости', value: '3', color: '#22c55e' },
  { label: 'Плотность потока', value: '4', color: '#f59e0b' },
  { label: 'Частота образования заторов', value: '5', color: '#8b5cf6' },
  { label: 'Скорость', value: '6', color: '#06b6d4' },
] as const

const TRANSPORT_LINES: LineSeries[] = [
  { label: 'Легковые', data: [], color: '#0f8fff' },
  { label: 'Грузовые', data: [], color: '#22c55e' },
  { label: 'Не распознано', data: [], color: '#f59e0b' },
]

const buildTransportSeries = (
  data: TrafficDetectorsChartsData
): LineSeries[] => [
  {
    label: 'Легковые',
    data: toChartPoints(data.passengerVehicle ?? []),
    color: '#FFB2AA',
  },
  {
    label: 'Грузовые',
    data: toChartPoints(data.truckVehicle ?? []),
    color: '#4DC7DD',
  },
  {
    label: 'Неопознанные',
    data: toChartPoints(data.unrecognizedVehicle ?? []),
    color: '#FFCC66',
  },
]

const DIRECTION_PAIR_COLORS: Record<
  '2' | '3' | '4' | '5',
  readonly [string, string]
> = {
  '2': ['#c026d3', '#333333'],
  '3': ['#15803d', '#22c55e'],
  '4': ['#b45309', '#f59e0b'],
  '5': ['#6d28d9', '#333333'],
}

const buildDirectionPairSeries = (
  data: TrafficDetectorsChartsData,
  chartValue: '2' | '3' | '4' | '5'
): LineSeries[] => {
  const [oppositeColor, sameColor] = DIRECTION_PAIR_COLORS[chartValue]
  return [
    {
      label: 'Встречное направление',
      data: toChartPoints(data.oppositeDirection ?? []),
      color: oppositeColor,
    },
    {
      label: 'Попутное направление',
      data: toChartPoints(data.sameDirection ?? []),
      color: sameColor,
    },
  ]
}

const SPEED_LINE_COLORS = [
  '#0ea5e9',
  '#d946ef',
  '#22c55e',
  '#eab308',
  '#f97316',
  '#6366f1',
] as const

const buildSpeedSeries = (data: TrafficDetectorsChartsData): LineSeries[] => [
  {
    label: 'Встречное — сред.',
    data: toChartPoints(data.oppositeDirectionAverage ?? []),
    color: SPEED_LINE_COLORS[0],
  },
  {
    label: 'Встречное — макс.',
    data: toChartPoints(data.oppositeDirectionMax ?? []),
    color: SPEED_LINE_COLORS[1],
  },
  {
    label: 'Встречное — мин.',
    data: toChartPoints(data.oppositeDirectionMin ?? []),
    color: SPEED_LINE_COLORS[2],
  },
  {
    label: 'Попутное — сред.',
    data: toChartPoints(data.sameDirectionAverage ?? []),
    color: SPEED_LINE_COLORS[3],
  },
  {
    label: 'Попутное — макс.',
    data: toChartPoints(data.sameDirectionMax ?? []),
    color: SPEED_LINE_COLORS[4],
  },
  {
    label: 'Попутное — мин.',
    data: toChartPoints(data.sameDirectionMin ?? []),
    color: SPEED_LINE_COLORS[5],
  },
]

const emptyData = { type: 0 } satisfies TrafficDetectorsChartsData

const PLACEHOLDER_SERIES: Record<string, LineSeries[]> = {
  '1': TRANSPORT_LINES,
  '2': buildDirectionPairSeries(emptyData, '2'),
  '3': buildDirectionPairSeries(emptyData, '3'),
  '4': buildDirectionPairSeries(emptyData, '4'),
  '5': buildDirectionPairSeries(emptyData, '5'),
  '6': buildSpeedSeries(emptyData),
}

const getPlaceholderSeries = (value: string): LineSeries[] =>
  PLACEHOLDER_SERIES[value] ?? []

const NewCharts = () => {
  const { id } = useParams()
  const { fromDate, toDate } = useAppSelector(stateDeviceSelector)
  const [selected, setSelected] = useState('1')
  const [postTrafficDetectorsCharts] = usePostTrafficDetectorsChartsMutation()
  const [chartsData, setChartsData] = useState<Record<string, LineSeries[]>>({})

  const startDateKey = fromDate.toISOString()
  const endDateKey = toDate.toISOString()

  useEffect(() => {
    if (!id || Number.isNaN(Number(id))) return
    const categoryType = Number(selected)
    const startDate = startDateKey
    const endDate = endDateKey
    postTrafficDetectorsCharts({
      deviceId: Number(id),
      categoryType,
      startDate,
      endDate,
    })
      .unwrap()
      .then((res) => {
        if (!res.success || !res.data) return
        if (categoryType === 1) {
          setChartsData((prev) => ({
            ...prev,
            [selected]: buildTransportSeries(res.data),
          }))
          return
        }
        if (categoryType >= 2 && categoryType <= 5) {
          const chartValue = selected as '2' | '3' | '4' | '5'
          setChartsData((prev) => ({
            ...prev,
            [selected]: buildDirectionPairSeries(res.data, chartValue),
          }))
          return
        }
        if (categoryType === 6) {
          setChartsData((prev) => ({
            ...prev,
            [selected]: buildSpeedSeries(res.data),
          }))
        }
      })
      .catch(() => {})
  }, [id, selected, startDateKey, endDateKey, postTrafficDetectorsCharts])

  const currentSeries = chartsData[selected] ?? getPlaceholderSeries(selected)

  return (
    <Box
      display='flex'
      flexDirection='column'
      width='100%'
      bgcolor='#fff'
      sx={{
        borderTop: '1px solid #eee',
      }}
    >
      <Box
        display='flex'
        justifyContent='center'
        pt={1}
      >
        <FormControl>
          <RadioGroup
            row
            value={selected}
            onChange={(_, val) => setSelected(val)}
          >
            {CHART_OPTIONS.map(({ label, value, color }) => (
              <FormControlLabel
                key={value}
                value={value}
                control={
                  <Radio
                    size='small'
                    sx={{
                      color,
                      '&.Mui-checked': { color },
                    }}
                  />
                }
                label={label}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </Box>
      <SimpleLineChart series={currentSeries} />
    </Box>
  )
}

export default NewCharts
